import { Mail } from 'lucide-react'
import { useId, useState, type FormEvent } from 'react'
import { useAuth } from './auth'

export type TextosEnlace = {
  correo: string
  enviarEnlace: string
  enviando: string
  enviadoTitulo: string
  enviadoTexto: string
  usarOtroCorreo: string
  errorEnvio: string
}

type Fase = 'libre' | 'enviando' | 'enviado' | 'error'

/**
 * Acceso por enlace al correo, sin contraseña. Solo entran cuentas que ya existen (`crearCuenta` en falso)
 * o también quienes se registran (tienda). El aviso es el mismo exista o no el correo, para no revelar
 * quién tiene cuenta; solo un fallo de red o de límite de envíos se informa como error.
 */
export function FormularioEnlace({ textos, crearCuenta }: { textos: TextosEnlace; crearCuenta: boolean }) {
  const { cliente } = useAuth()
  const idCorreo = useId()
  const [correo, setCorreo] = useState('')
  const [fase, setFase] = useState<Fase>('libre')

  async function enviar(e: FormEvent) {
    e.preventDefault()
    if (!cliente || fase === 'enviando') return
    setFase('enviando')
    const { error } = await cliente.auth.signInWithOtp({
      email: correo.trim().toLowerCase(),
      options: { shouldCreateUser: crearCuenta, emailRedirectTo: window.location.origin },
    })
    const esFalloReal = error !== null && (error.status === 429 || error.status === undefined || error.status >= 500)
    setFase(esFalloReal ? 'error' : 'enviado')
  }

  if (fase === 'enviado') {
    return (
      <div className="aviso-vacio" role="status">
        <h2>{textos.enviadoTitulo}</h2>
        <p>{textos.enviadoTexto}</p>
        <p className="accion">
          <button type="button" className="boton boton--secundario" onClick={() => setFase('libre')}>
            {textos.usarOtroCorreo}
          </button>
        </p>
      </div>
    )
  }

  return (
    <form className="formulario" onSubmit={(e) => void enviar(e)}>
      <div className="campo">
        <label htmlFor={idCorreo}>{textos.correo}</label>
        <input
          id={idCorreo}
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          required
          value={correo}
          onChange={(e) => setCorreo(e.target.value)}
          aria-invalid={fase === 'error'}
          aria-describedby={fase === 'error' ? `${idCorreo}-error` : undefined}
        />
        {fase === 'error' && (
          <p id={`${idCorreo}-error`} className="campo__error" role="alert">
            {textos.errorEnvio}
          </p>
        )}
      </div>
      <button type="submit" className="boton boton--primario" disabled={fase === 'enviando'} aria-busy={fase === 'enviando'}>
        <Mail className="icono" strokeWidth={1.75} aria-hidden="true" />
        {fase === 'enviando' ? textos.enviando : textos.enviarEnlace}
      </button>
    </form>
  )
}
